import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react";
import { Container, Row, Col, Card } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import AddMembershipPlanModal from "./addMembershipPlanModal";
import { getAllMembershipPlans } from "../../../store/slices/membershipPlansSlice";
import { useDebounce } from "../../../Hooks/useDebounce";

const normalizeMembershipPlans = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.membershipPlans)) {
    return payload.membershipPlans;
  }

  if (Array.isArray(payload?.clients)) {
    return payload.clients;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const getPlanName = (row) => row?.name || row?.client_name || "-";

const getSecurityItems = (row) => {
  if (
    Array.isArray(row?.security_provider_names) &&
    row.security_provider_names.length
  ) {
    return row.security_provider_names.filter(Boolean);
  }

  if (Array.isArray(row?.security_providers) && row.security_providers.length) {
    return row.security_providers
      .map((provider) => provider?.name)
      .filter(Boolean);
  }

  const normalizeItems = (items) =>
    items
      .flatMap((item) => {
        if (typeof item !== "string") {
          return item;
        }

        const trimmedItem = item.trim();

        if (trimmedItem.startsWith("[") && trimmedItem.endsWith("]")) {
          try {
            const parsedItem = JSON.parse(trimmedItem);
            return Array.isArray(parsedItem) ? parsedItem : [parsedItem];
          } catch (error) {
            return trimmedItem.replace(/^\[/, "").replace(/\]$/, "").split(",");
          }
        }

        return trimmedItem;
      })
      .map((item) =>
        typeof item === "string" ? item.replace(/^"|"$/g, "").trim() : item,
      )
      .filter(Boolean);

  const normalizeStringValue = (value) => {
    const trimmedValue = value.trim();

    if (trimmedValue.startsWith("[") && trimmedValue.endsWith("]")) {
      try {
        const parsedValue = JSON.parse(trimmedValue);
        return Array.isArray(parsedValue)
          ? normalizeItems(parsedValue)
          : normalizeItems([parsedValue]);
      } catch (error) {
        return normalizeItems([
          trimmedValue.replace(/^\[/, "").replace(/\]$/, ""),
        ]);
      }
    }

    return normalizeItems(
      trimmedValue
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    );
  };

  if (Array.isArray(row?.security_options) && row.security_options.length) {
    return normalizeItems(row.security_options);
  }

  if (
    typeof row?.security_options === "string" &&
    row.security_options.trim()
  ) {
    return normalizeStringValue(row.security_options);
  }

  if (Array.isArray(row?.security) && row.security.length) {
    return normalizeItems(row.security);
  }

  if (typeof row?.security === "string" && row.security.trim()) {
    return normalizeStringValue(row.security);
  }

  return [];
};

const ManageMembershipPlansTable = () => {
  const dispatch = useDispatch();
  const client = useSelector((state) => state.clients?.selectedClient?.value);
  const [data, setData] = useState([]);
  const [totalCount, setCount] = useState(0);
  const [editItem, setEditItem] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);

  const handlePageChange = useCallback((event) => {
    setPagination(event);
  }, []);

  const getMembershipPlans = async (payload) => {
    try {
      const response = await dispatch(getAllMembershipPlans(payload));
      if (response?.payload) {
        const membershipPlans = normalizeMembershipPlans(response.payload);
        setData(membershipPlans);
        setCount(response.payload?.count ?? membershipPlans.length);
      }
    } catch (error) {
      console.error("Error fetching interactions:", error);
    }
  };

  const handleEdit = (clientData) => {
    console.log("Edit membership plan row data:", clientData);
    setEditItem(clientData);
    setModalOpen(true);
  };
  const handleAdd = () => {
    setEditItem({});
    setModalOpen(true);
  };

  const handleOnChangeSearch = (value) => {
    const nextValue = typeof value === "function" ? value(searchQuery) : value;
    setSearchQuery(nextValue);
    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // Reset to first page when search query changes
  };

  const columns = useMemo(
    () => [
      { accessorKey: "id", header: "Plan ID", size: 150 },
      { accessorKey: "country", header: "Country", size: 150 },
      {
        id: "name",
        header: "Name",
        size: 150,
        accessorFn: (row) => getPlanName(row),
      },
      {
        id: "security",
        header: "Security",
        size: 220,
        accessorFn: (row) => getSecurityItems(row).join(", "),
        Cell: ({ row }) => {
          const securityItems = getSecurityItems(row.original);

          if (!securityItems.length) {
            return <span className="text-muted">-</span>;
          }

          return (
            <div className="d-flex gap-1 flex-wrap">
              {securityItems.map((item) => (
                <span key={item} className="badge bg-primary">
                  {item}
                </span>
              ))}
            </div>
          );
        },
      },
      { accessorKey: "created_at", header: "Added On", size: 150 },
      {
        id: "action",
        header: "Action",
        size: 140,
        enableSorting: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        Cell: ({ row }) => (
          <button
            type="button"
            className="btn btn-primary p-1"
            aria-label="Edit membership plan"
            title="Edit"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row.original);
            }}
          >
            <i className="fa fa-edit"></i>
          </button>
        ),
      },
    ],
    [],
  );

  useEffect(() => {
    getMembershipPlans({ searchQuery: debouncedSearch });
  }, [dispatch, columns, debouncedSearch]);

  const table = useMaterialReactTable({
    columns,
    data,
    manualFiltering: true,
    onPaginationChange: handlePageChange,
    state: {
      pagination,
      globalFilter: searchQuery,
    },
    onGlobalFilterChange: handleOnChangeSearch,
    renderTopToolbarCustomActions: () => (
      <div className="d-flex">
        <button
          className="btn bg-primary"
          disabled={client === "ts"}
          onClick={handleAdd}
        >
          Add Membership Plan
        </button>
      </div>
    ),
  });

  const refreshMembershipPlans = useCallback(() => {
    getMembershipPlans({ searchQuery: debouncedSearch });
  }, [debouncedSearch]);

  const toggleCreateModal = () => {
    setModalOpen(!isModalOpen);
    if (isModalOpen) {
      setEditItem({}); // Clear data when closing
    }
  };

  return (
    <Fragment>
      <AddMembershipPlanModal
        isOpen={isModalOpen}
        toggle={toggleCreateModal}
        editItem={editItem}
        onSuccess={refreshMembershipPlans}
      />
      <Breadcrumbs
        mainTitle="Membership Plan"
        parent="Membership Plan"
        title="Membership Plan"
      />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <MaterialReactTable table={table} />
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ManageMembershipPlansTable;
